import {inquiryPath, sickReportPath} from '@/helpers/substitutions/overview';
import moment from 'moment';
import {NotificationLike} from '@/types/NotificationLike.type';
import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {LogEntryObjectTypes} from '@/enums/LogEntryEnums';
import {ToDoDataTypesEnum} from '@/enums/to-do-manager/ToDoDataTypes.enum';
import {NotificationEntry} from '@/helpers/notificationGroups.helper';
import {courseDatesText} from '@/helpers/missingCourseLogs.helper';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {TakeoverOfferUpdateEvents} from '@/enums/substitution-manager/TakeoverOfferUpdateEvents';
import {notificationPortal, portalNotificationPath} from '@/components/teacherPortal/portalNotificationPaths';

moment.locale('de');

/** Segments of the notification panel. "Erwähnungen" are the notifications of type `mention` (@mention in a log entry). */
export type NotificationSegment = 'open' | 'mentions' | 'dismissed';

export type NotificationTone = 'info' | 'good' | 'warn' | 'bad' | 'neutral' | 'mention';

export type NotificationLinkAction = {
    /** Router path, opened in a new tab */
    path: string;
    icon: string;
    label: string;
};

export type NotificationView = {
    icon: string;
    tone: NotificationTone;
    title: string;
    text?: string;
    /** Additional plain lines (e.g. errors of a course cancellation, completion comment) */
    lines: string[];
    /** Highlighted line, e.g. due date of an overdue to-do */
    hint?: { text: string; tone: NotificationTone };
    /** Router path opened by a click on the row */
    path?: string;
    /** Hover actions that open a target in a new tab */
    links: NotificationLinkAction[];
    /** To-do notifications for a school offer the acquisition list button */
    acquisitionLists: boolean;
};

type Payload = Record<string, unknown>;

const str = (value: unknown): string => (value === undefined || value === null ? '' : String(value));

const payloadOf = (notification: NotificationLike): Payload => {
    const payload = (notification as { messagePayload?: unknown }).messagePayload;
    return payload && typeof payload === 'object' ? payload as Payload : {};
};

/** Plain text of an HTML log entry. DOMParser does not execute scripts or load images. */
export const htmlToText = (html: string): string => {
    if (!html) {
        return '';
    }
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
        return html;
    }
};

const entityPath = (payload: Payload): string | undefined => {
    const type = str(payload.entityType);
    const id = str(payload.entityId);
    return type && id ? `/${type}/${id}` : undefined;
};

const MENTION_PATHS: Record<string, string> = {
    [LogEntryObjectTypes.EMPLOYEE]: 'employees',
    [LogEntryObjectTypes.INSTITUTION]: 'institutions',
    [LogEntryObjectTypes.PARENT]: 'parents',
    [LogEntryObjectTypes.STUDENT]: 'students',
};

const newTab = (path: string | undefined, label: string, icon = 'mdi-open-in-new'): NotificationLinkAction[] =>
    path ? [{path, icon, label}] : [];

const base = (icon: string, tone: NotificationTone, title: string): NotificationView => ({
    icon, tone, title, lines: [], links: [], acquisitionLists: false,
});

/** Profile of the mention; with the log entry id the timeline scrolls to the entry and highlights it (`?log=`) */
export const mentionPath = (payload: Payload, logEntryId?: unknown): string | undefined => {
    const folder = MENTION_PATHS[str(payload.objectType)];
    if (!folder || !payload.objectId) {
        return undefined;
    }
    const path = `/${folder}/${str(payload.objectId)}`;
    return logEntryId ? `${path}?log=${encodeURIComponent(str(logEntryId))}` : path;
};

const mentionView = (payload: Payload, logEntryId?: unknown): NotificationView => {
    const from = str(payload.fromName);
    const path = mentionPath(payload, logEntryId);
    return {
        ...base('mdi-at', 'mention', from ? `${from} hat dich erwähnt` : 'Du wurdest erwähnt'),
        text: htmlToText(str(payload.message)),
        lines: payload.objectName ? [str(payload.objectName)] : [],
        path,
        links: newTab(path, 'Eintrag in neuem Tab öffnen'),
    };
};

const toDoDataText = (payload: Payload): string | undefined => {
    const data = payload.data;
    if (data === undefined || data === null) {
        return undefined;
    }
    if (typeof data === 'boolean') {
        return data ? 'Ja' : 'Nein';
    }
    if (payload.dataType === ToDoDataTypesEnum.DATE) {
        return moment(data as Date).format('DD.MM.YYYY');
    }
    if (Array.isArray(data)) {
        return data.join(', ');
    }
    return str(data);
};

/** "fällig seit 22.10.2025" (date-only) or "fällig seit 3 Stunden" */
const overdueHint = (payload: Payload): string => {
    const due = moment(payload.dueDate as Date);
    if (!payload.hasDueTime) {
        return `fällig seit ${due.format('DD.MM.YYYY')}`;
    }
    return `fällig seit ${due.fromNow(true)}`;
};

const toDoView = (type: NotificationTypes, payload: Payload): NotificationView => {
    const toDoPath = payload.toDoId ? `/todos/${str(payload.toDoId)}` : undefined;
    const entity = entityPath(payload);
    const view: NotificationView = {
        ...base('mdi-checkbox-marked-circle-outline', 'info', 'Neue Aufgabe'),
        text: str(payload.name),
        path: toDoPath || entity,
        links: [...newTab(toDoPath, 'Aufgabe in neuem Tab öffnen'), ...newTab(entity, 'Ansehen', 'mdi-page-next-outline')],
        acquisitionLists: true,
    };
    if (type === NotificationTypes.TO_DO_ASSIGNED) {
        view.icon = 'mdi-clipboard-account-outline';
        view.title = payload.fromName ? `Neue Aufgabe von ${str(payload.fromName)}` : 'Neue Aufgabe vom System';
    } else if (type === NotificationTypes.TO_DO_COMPLETED) {
        view.icon = 'mdi-check-circle-outline';
        view.tone = 'good';
        view.title = `${str(payload.responsibleName) || 'Jemand'} hat eine Aufgabe erledigt`;
        view.lines = payload.completionComment ? [`Kommentar: ${str(payload.completionComment)}`] : [];
        const data = toDoDataText(payload);
        view.hint = data ? {text: data, tone: 'good'} : undefined;
    } else if (type === NotificationTypes.DUE_TO_DO) {
        view.icon = 'mdi-calendar-today';
        view.tone = 'warn';
        view.title = 'Heute fällig';
        view.hint = payload.dueDate && payload.hasDueTime
            ? {text: `fällig um ${moment(payload.dueDate as Date).format('HH:mm')} Uhr`, tone: 'warn'} : undefined;
    } else {
        view.icon = 'mdi-alarm';
        view.tone = 'bad';
        view.title = 'To-do überfällig';
        view.hint = payload.dueDate ? {text: overdueHint(payload), tone: 'bad'} : undefined;
    }
    return view;
};

const WHATSAPP_STATES: Record<string, { text: string; icon: string; tone: NotificationTone }> = {
    REJECTED: {text: 'abgelehnt', icon: 'mdi-close-circle-outline', tone: 'bad'},
    APPROVED: {text: 'genehmigt', icon: 'mdi-check-circle-outline', tone: 'good'},
    PENDING: {text: 'ausstehend', icon: 'mdi-clock-outline', tone: 'warn'},
    PAUSED: {text: 'von Meta pausiert', icon: 'mdi-pause-circle-outline', tone: 'warn'},
    DISABLED: {text: 'von Meta gesperrt', icon: 'mdi-cancel', tone: 'bad'},
    IN_APPEAL: {text: 'im Widerspruch', icon: 'mdi-scale-balance', tone: 'warn'},
    FLAGGED: {text: 'mit niedriger Qualität markiert', icon: 'mdi-flag-outline', tone: 'warn'},
    PENDING_DELETION: {text: 'wird gelöscht', icon: 'mdi-delete-clock-outline', tone: 'neutral'},
    DELETED: {text: 'bei Meta gelöscht', icon: 'mdi-delete-outline', tone: 'bad'},
    LIMIT_EXCEEDED: {text: 'Limit erreicht', icon: 'mdi-alert-outline', tone: 'bad'},
};

const whatsAppView = (payload: Payload): NotificationView => {
    const state = WHATSAPP_STATES[str(payload.status)] || {text: 'unbekannt', icon: 'mdi-help-circle-outline', tone: 'neutral'};
    const path = payload.configurationId ? `/templates/whatsapp/${str(payload.configurationId)}` : undefined;
    return {
        ...base(state.icon, state.tone, 'Aktualisierung der Nachrichtenvorlage'),
        text: `${str(payload.name)} ${state.text}`.trim(),
        lines: payload.reason ? [str(payload.reason)] : [],
        path,
        links: newTab(path, 'Vorlagenübersicht in neuem Tab öffnen'),
    };
};

export const courseCancellationView = (payload: Payload): NotificationView => {
    const students = Number(payload.studentErrorCount) || 0;
    const lines = [
        payload.institutionErrorCount ? 'Probleme bei Standort' : '',
        students ? `Probleme bei ${students} ${students === 1 ? 'Schüler:in' : 'Schüler:innen'}` : '',
        payload.teacherErrorCount ? 'Probleme bei Lehrkraft' : '',
    ].filter(Boolean);
    // opens the inquiry in the overview (date filter widened when needed)
    const path = payload.substitutionInquiryId ? inquiryPath(str(payload.substitutionInquiryId)) : undefined;
    return {...base('mdi-alert-outline', 'bad', 'Fehler bei Kursabsage'), lines, path, links: newTab(path, 'In neuem Tab öffnen')};
};

/** Versand: Stand einer DHL-Abholung geändert (an die buchende Person) */
const PICKUP_STATES: Record<string, { title: string; tone: NotificationTone; icon: string }> = {
    accepted: {title: 'DHL-Abholung bestätigt', tone: 'good', icon: 'mdi-truck-check-outline'},
    picked_up: {title: 'DHL hat abgeholt', tone: 'good', icon: 'mdi-truck-check-outline'},
    partial: {title: 'DHL hat nur teilweise abgeholt', tone: 'warn', icon: 'mdi-truck-alert-outline'},
    not_picked_up: {title: 'DHL hat nicht abgeholt', tone: 'bad', icon: 'mdi-truck-remove-outline'},
    rejected: {title: 'DHL hat die Abholung abgelehnt', tone: 'bad', icon: 'mdi-truck-remove-outline'},
    delayed: {title: 'DHL-Abholung verspätet', tone: 'warn', icon: 'mdi-truck-alert-outline'},
    cancelled: {title: 'DHL-Abholung storniert', tone: 'neutral', icon: 'mdi-truck-outline'},
    requested: {title: 'DHL-Abholung: Stand geändert', tone: 'info', icon: 'mdi-truck-outline'},
};

export const dhlPickupView = (payload: Payload): NotificationView => {
    const meta = PICKUP_STATES[str(payload.state)] || PICKUP_STATES.requested;
    const day = payload.pickupDay ? moment(str(payload.pickupDay), 'YYYY-MM-DD').format('dd, DD.MM.') : '';
    const parcels = Number(payload.parcels) || 0;
    const lines = [
        [day ? `Abholung am ${day}` : '', parcels ? `${parcels} ${parcels === 1 ? 'Paket' : 'Pakete'}` : ''].filter(Boolean).join(' · '),
        payload.timeWindow ? `Zeitfenster laut DHL: ${str(payload.timeWindow)} Uhr` : '',
        payload.state === 'not_picked_up' || payload.state === 'rejected' ? 'Bitte neu buchen oder die Pakete selbst zur Post bringen.' : '',
    ].filter(Boolean);
    const path = '/warehouse/shipping?tab=pickups';
    return {...base(meta.icon, meta.tone, meta.title), lines, path, links: newTab(path, 'In neuem Tab öffnen')};
};

const supervisorView = (type: NotificationTypes, payload: Payload): NotificationView => {
    const path = entityPath(payload);
    const name = str(payload.entityName);
    if (type === NotificationTypes.ENTITY_ASSIGNED_TO_SUPERVISOR) {
        return {
            ...base('mdi-account-arrow-left-outline', 'info', 'Neue Zuweisung'),
            text: `${name} wurde dir zugewiesen`,
            path,
            links: newTab(path, 'In neuem Tab öffnen'),
        };
    }
    const date = payload.dateOfDeactivation ? moment(payload.dateOfDeactivation as Date) : undefined;
    const done = !!date && date.isBefore(moment().add(1, 'day').startOf('day'));
    return {
        ...base(payload.entityType === 'employees' ? 'mdi-account-off-outline' : 'mdi-domain-off', 'bad',
            `${name} ${done ? 'wurde' : 'wird'} deaktiviert`),
        text: date ? `Deaktivierung ${done ? 'am' : 'zum'} ${date.format('DD.MM.YYYY')}` : undefined,
        path,
        links: newTab(path, 'In neuem Tab öffnen'),
    };
};

const handoutFeedbackView = (payload: Payload): NotificationView => {
    const author = payload.authorType === 'institution' ? 'Standort' : 'Werbende Person';
    const action = payload.actionType === 'create' ? 'abgegeben' : 'bearbeitet';
    const path = payload.handoutEventId ? `/planning/handout-events/${str(payload.handoutEventId)}` : undefined;
    return {
        ...base('mdi-message-reply-text-outline', 'info', `${author} hat Feedback ${action}`),
        text: `Austeilaktion ${str(payload.institutionName)}`.trim(),
        path,
        links: newTab(path, 'In neuem Tab öffnen'),
    };
};

/** Lessons shown in the text of the missing log reminder, the rest as "+ N weitere" */
const MISSING_LOG_LESSONS_SHOWN = 3;

/** Today view with the open logs: newest lesson day, filter "Offene Protokolle", backlog expanded */
export const missingLogsPath = (newestDay?: string): string => {
    const query = ['filter=open', 'backlog=1', ...(newestDay ? [`day=${encodeURIComponent(newestDay)}`] : [])];
    return `/today?${query.join('&')}`;
};

const missingCourseLogsView = (payload: Payload): NotificationView => {
    const count = Number(payload.lessonCount) || 0;
    const notBillable = Number(payload.notBillableCount) || 0;
    const lessons = Array.isArray(payload.lessons) ? payload.lessons as Payload[] : [];
    const shown = lessons.slice(0, MISSING_LOG_LESSONS_SHOWN)
        .map((lesson) => `${str(lesson.customId)} ${moment(str(lesson.date), 'YYYY-MM-DD').format('dd DD.MM.')}`);
    const more = count - shown.length;
    const path = missingLogsPath(lessons.length ? str(lessons[0].date) : undefined);
    return {
        ...base('mdi-clipboard-clock-outline', 'warn', `${courseDatesText(count)} ${count === 1 ? 'wartet' : 'warten'} auf dein Protokoll`),
        text: [shown.join(' · '), more > 0 ? `+ ${more} weitere` : ''].filter(Boolean).join(' '),
        lines: payload.fromName ? [`Erinnerung von ${str(payload.fromName)}`] : [],
        hint: notBillable ? {
            text: notBillable === count ? 'noch nicht abrechenbar' : `davon ${courseDatesText(notBillable)} noch nicht abrechenbar`,
            tone: 'warn',
        } : undefined,
        path,
        links: newTab(path, 'Jetzt protokollieren (neuer Tab)', 'mdi-clipboard-edit-outline'),
    };
};

/** Monitoring of the takeover offers with this offer (no import of the takeover helper: it would create an import cycle via the API classes) */
export const takeoverOfferPath = (offerId: unknown): string => `/takeover-offers?offer=${encodeURIComponent(str(offerId))}`;

const TAKEOVER_EVENTS: Record<string, { icon: string; tone: NotificationTone; title: string }> = {
    [TakeoverOfferUpdateEvents.ACCEPTED]: {icon: 'mdi-account-check-outline', tone: 'good', title: 'Übernahme zugesagt'},
    [TakeoverOfferUpdateEvents.DECLINED]: {icon: 'mdi-account-remove-outline', tone: 'neutral', title: 'Übernahme abgelehnt'},
    [TakeoverOfferUpdateEvents.ALL_DECLINED]: {icon: 'mdi-account-cancel-outline', tone: 'bad', title: 'Alle haben abgelehnt'},
    [TakeoverOfferUpdateEvents.EXPIRED]: {icon: 'mdi-clock-alert-outline', tone: 'warn', title: 'Übernahme-Angebot abgelaufen'},
    [TakeoverOfferUpdateEvents.CONFIRMED]: {icon: 'mdi-check-decagram-outline', tone: 'good', title: 'Übernahme eingetragen'},
    [TakeoverOfferUpdateEvents.ACCEPTANCE_REJECTED]: {icon: 'mdi-account-cancel-outline', tone: 'warn', title: 'Zusage abgelehnt'},
};

const takeoverOfferView = (payload: Payload): NotificationView => {
    const event = str(payload.event);
    const info = TAKEOVER_EVENTS[event] || {icon: 'mdi-account-convert-outline', tone: 'info' as NotificationTone, title: 'Übernahme-Angebot'};
    const name = str(payload.employeeName) || 'Jemand';
    // weekdays concerned (per-day answers: only the accepted / confirmed / rejected weekdays)
    const dayNames = (Array.isArray(payload.weekdays) ? payload.weekdays as number[] : [])
        .map((day) => Weekdays.Record[day as Weekdays]?.text).filter(Boolean);
    const days = dayNames.join(', ');
    const offer = `${str(payload.institutionName) || 'Standort'}${days ? ` (${days})` : ''}`;
    const date = payload.startDate ? moment(payload.startDate as Date).format('DD.MM.YYYY') : '';
    const from = date ? ` ab ${date}` : '';
    const texts: Record<string, string> = {
        [TakeoverOfferUpdateEvents.ACCEPTED]: `${name} hat die Übernahme ${offer}${from} zugesagt – bitte prüfen`,
        [TakeoverOfferUpdateEvents.DECLINED]: `${name} hat die Übernahme ${offer}${from} abgelehnt`,
        [TakeoverOfferUpdateEvents.ALL_DECLINED]: `Alle Kandidat:innen haben die Übernahme ${offer}${from} abgelehnt`,
        [TakeoverOfferUpdateEvents.EXPIRED]: `Das Übernahme-Angebot ${offer}${from} ist ohne Zusage abgelaufen`,
        [TakeoverOfferUpdateEvents.CONFIRMED]: `Die Übernahme ${offer}${from} durch ${name} wurde eingetragen`,
        [TakeoverOfferUpdateEvents.ACCEPTANCE_REJECTED]: `Die Zusage von ${name} für ${offer} wurde abgelehnt – `
            + `${dayNames.length > 1 ? 'die Tage sind' : 'der Tag ist'} wieder offen`,
    };
    const path = payload.takeoverOfferId ? takeoverOfferPath(payload.takeoverOfferId) : undefined;
    const institution = payload.institutionId ? `/institutions/${str(payload.institutionId)}` : undefined;
    return {
        ...base(info.icon, info.tone, info.title),
        text: texts[event] || `Übernahme ${offer}${from}`,
        hint: event === TakeoverOfferUpdateEvents.ACCEPTED ? {text: 'Bestätigen & eintragen oder ablehnen', tone: 'good'} : undefined,
        path,
        links: [...newTab(path, 'Angebot in neuem Tab öffnen'), ...newTab(institution, 'Standort ansehen', 'mdi-domain')],
    };
};

/** Ausfall gemeldet / Vertretung zurückgezogen (Lehrkraft-Bereich): to the inquiries of the teacher */
export const teacherSickReportView = (payload: Payload): NotificationView => {
    const withdrawn = str(payload.event) === 'substitutionWithdrawn';
    const name = str(payload.employeeName) || 'Eine Lehrkraft';
    const employee = payload.employeeId ? `/employees/${encodeURIComponent(str(payload.employeeId))}` : undefined;
    if (str(payload.event) === 'handoutUnavailable') {
        // Austeilaktion of the reporting teacher marked as "fällt aus": to the event (re-staff)
        const handout = payload.handoutEventId ? `/planning/handout-events/${encodeURIComponent(str(payload.handoutEventId))}` : undefined;
        return {
            ...base('mdi-account-off-outline', 'bad', `Austeilaktion: ${name} fällt aus`),
            text: str(payload.text),
            hint: payload.hasToDo ? {text: 'To-do: Austeilaktion neu besetzen oder absagen', tone: 'warn'} : undefined,
            path: handout,
            links: [...newTab(handout, 'Austeilaktion in neuem Tab öffnen'), ...newTab(employee, 'Lehrkraft ansehen', 'mdi-account-outline')],
        };
    }
    // opens the affected inquiry; without one (older notifications) the inquiries of the teacher from the reported day
    const path = payload.inquiryId ? inquiryPath(str(payload.inquiryId))
        : payload.sickReportId ? sickReportPath(str(payload.sickReportId)) : '/substitution-inquiries-table';
    // follow-ups of a sick report (same event, text of the backend): nobody could be asked / everybody declined
    const allDeclined = !withdrawn && str(payload.text).includes('Alle Angefragten haben abgesagt');
    const noCandidates = !withdrawn && str(payload.text).includes('Keine Vertretung gefunden');
    const title = withdrawn ? `${name} hat eine Vertretung zurückgezogen`
        : allDeclined ? `Ausfall ${name}: Alle Angefragten haben abgesagt`
            : noCandidates ? `${name} hat einen Ausfall gemeldet – keine Vertretung gefunden` : `${name} hat einen Ausfall gemeldet`;
    const decide = allDeclined || noCandidates;
    return {
        ...base(withdrawn ? 'mdi-account-arrow-left-outline' : 'mdi-account-alert-outline', withdrawn ? 'warn' : 'bad', title),
        text: str(payload.text),
        hint: payload.hasToDo
            ? {text: decide ? 'To-do: Vertretung suchen, Stunde absagen oder automatische Absage stoppen' : 'To-do: Vertretung suchen oder absagen', tone: 'warn'}
            : undefined,
        path,
        links: [...newTab(path, 'Vertretungsanfrage in neuem Tab öffnen'), ...newTab(employee, 'Lehrkraft ansehen', 'mdi-account-outline')],
    };
};

/** Target of the QM-Cockpit summary (an own path of the payload only inside the cockpit) */
export const QUALITY_COCKPIT_PATH = '/quality-management';

/** QM-Cockpit summary: "3 neue Rückgewinnungen, 1 Gruppenfall" */
/**
 * "Bewerbung zurückgezogen" / "Bewerbung abgelehnt" (kind): who, optional reason / who rejected, planning warning; click opens the
 * applicant profile – with something left to clean up directly the dialog „Aufräumen …“ (?cleanup=1)
 */
export const applicationWithdrawnView = (payload: Payload): NotificationView => {
    const name = str(payload.employeeName) || 'Eine Bewerberin / ein Bewerber';
    const id = str(payload.employeeId);
    const rejected = str(payload.kind) === 'rejected';
    const path = str(payload.path).startsWith('/recruiter/applicants/') ? str(payload.path) : (id ? `/recruiter/applicants/${id}` : undefined);
    const cleanup = Number(payload.cleanupCount) || 0;
    const planned = cleanup > 0 || (payload.cleanupCount === undefined
        && (Number(payload.acquisitionListCount) || 0) + (Number(payload.institutionCount) || 0) + (Number(payload.courseCount) || 0) > 0);
    const lines = [
        ...(str(payload.reason) ? [`Grund: ${str(payload.reason)}`] : []),
        ...(rejected && str(payload.closedByName) ? [`Abgelehnt von ${str(payload.closedByName)}`] : []),
    ];
    return {
        ...base(rejected ? 'mdi-account-cancel-outline' : 'mdi-account-arrow-left-outline', planned ? 'warn' : 'neutral', rejected ? 'Bewerbung abgelehnt' : 'Bewerbung zurückgezogen'),
        text: rejected ? `Die Bewerbung von ${str(payload.employeeName) || 'einer Person'} wurde abgelehnt.` : `${name} hat die Bewerbung zurückgezogen.`,
        lines,
        ...(planned ? {hint: {text: `${str(payload.summary) || 'Noch eingeplant'} – bitte aufräumen`, tone: 'warn' as NotificationTone}} : {}),
        path,
        links: path ? [{path, icon: planned ? 'mdi-broom' : 'mdi-account-outline', label: planned ? 'Aufräumen öffnen' : 'Bewerberprofil öffnen'}] : [],
    };
};

/** "Löschantrag (DSGVO)": the person wants account + data deleted, the office decides (Bewerberprofil) */
export const applicantDeletionRequestedView = (payload: Payload): NotificationView => {
    const name = str(payload.employeeName) || 'Eine Person';
    const id = str(payload.employeeId);
    const path = str(payload.path).startsWith('/recruiter/applicants/') ? str(payload.path) : (id ? `/recruiter/applicants/${id}` : undefined);
    return {
        ...base('mdi-account-cancel-outline', 'warn', 'Löschantrag (DSGVO)'),
        text: `${name} möchte Konto und Daten löschen.`,
        lines: str(payload.blockers) ? [`Selbstlöschung nicht möglich wegen: ${str(payload.blockers)}`] : [],
        hint: {text: 'Aufbewahrungspflichten prüfen, dann im Bewerberprofil anonymisieren oder stoppen', tone: 'warn' as NotificationTone},
        path,
        links: path ? [{path, icon: 'mdi-account-outline', label: 'Bewerberprofil öffnen'}] : [],
    };
};

/**
 * Text of a summary: the workflow-manager text names every kind after rescue / group ("…, 1 Lehrkraft: Scorecard
 * auffällig") and is used when it starts with the counted parts; otherwise (older / foreign text) the counted parts.
 */
const digestViewText = (own: string, text: string): string => {
    if (own) {
        return text.startsWith(own) ? text : own;
    }
    return text;
};

/** Summary with counts per QM case type (workflow-manager since 12.10.): its text "3 neue Fälle: 1 Rückgewinnung, …" */
const hasTypeCounts = (payload: Payload): boolean => !!payload.typeCounts && typeof payload.typeCounts === 'object'
    && Object.values(payload.typeCounts as Record<string, unknown>).some((n) => Number(n) > 0);

const qualityCaseDigestView = (payload: Payload): NotificationView => {
    const rescue = Number(payload.rescueCount) || 0;
    const group = Number(payload.groupCount) || 0;
    const parts = [
        rescue ? `${rescue} neue ${rescue === 1 ? 'Rückgewinnung' : 'Rückgewinnungen'}` : '',
        group ? `${group} ${group === 1 ? 'Gruppenfall' : 'Gruppenfälle'}` : '',
    ].filter(Boolean);
    const path = str(payload.path).startsWith(QUALITY_COCKPIT_PATH) ? str(payload.path) : QUALITY_COCKPIT_PATH;
    return {
        ...base('mdi-account-heart-outline', 'info', 'Neue Fälle im QM-Cockpit'),
        text: hasTypeCounts(payload) && str(payload.text) ? str(payload.text) : digestViewText(parts.join(', '), str(payload.text)),
        lines: ['Die Aufgaben stehen in deiner To-do-Liste.'],
        path,
        links: newTab(path, 'QM-Cockpit in neuem Tab öffnen'),
    };
};

/** Icon, texts and (office) targets of one notification, independent of its type */
const officeNotificationView = (notification: NotificationLike): NotificationView => {
    const payload = payloadOf(notification);
    switch (notification.type) {
        case NotificationTypes.MENTION:
            return mentionView(payload, (notification as { logEntry?: unknown }).logEntry);
        case NotificationTypes.OVERDUE_TO_DO:
        case NotificationTypes.DUE_TO_DO:
        case NotificationTypes.TO_DO_ASSIGNED:
        case NotificationTypes.TO_DO_COMPLETED:
            return toDoView(notification.type, payload);
        case NotificationTypes.CALL: {
            const path = entityPath(payload);
            return {
                ...base('mdi-phone-incoming-outline', 'info', 'Eingehender Anruf'),
                text: str(payload.fromName) || 'Unbekannter Anrufer',
                path,
                links: newTab(path, 'Profil in neuem Tab öffnen'),
            };
        }
        case NotificationTypes.SUPERVISED_ENTITY_DEACTIVATED:
        case NotificationTypes.ENTITY_ASSIGNED_TO_SUPERVISOR:
            return supervisorView(notification.type, payload);
        case NotificationTypes.WHATSAPP_MESSAGE_TEMPLATE_STATUS_UPDATE:
            return whatsAppView(payload);
        case NotificationTypes.COURSE_CANCELLATION_FAILED:
            return courseCancellationView(payload);
        case NotificationTypes.HANDOUT_EVENT_FEEDBACK:
            return handoutFeedbackView(payload);
        case NotificationTypes.MISSING_COURSE_LOGS:
            return missingCourseLogsView(payload);
        case NotificationTypes.TAKEOVER_OFFER_UPDATE:
            return takeoverOfferView(payload);
        case NotificationTypes.QUALITY_CASE_DIGEST:
            return qualityCaseDigestView(payload);
        case NotificationTypes.TEACHER_SICK_REPORT:
            return teacherSickReportView(payload);
        case NotificationTypes.APPLICATION_WITHDRAWN:
            return applicationWithdrawnView(payload);
        case NotificationTypes.DHL_PICKUP_UPDATE:
            return dhlPickupView(payload);
        case NotificationTypes.APPLICANT_DELETION_REQUESTED:
            return applicantDeletionRequestedView(payload);
        case NotificationTypes.COMMON:
            return {...base('mdi-bell-outline', 'neutral', 'Mitteilung'), text: str((notification as { message?: unknown }).message)};
        default:
            return base('mdi-bell-outline', 'neutral', 'Benachrichtigung');
    }
};

/**
 * Icon, texts and targets of one notification. In the Lehrkraft-/Bewerbungs-Bereich the office targets are mapped to the portal
 * pages (portalNotificationPaths.ts); targets without portal page are dropped.
 */
export const toNotificationView = (notification: NotificationLike): NotificationView => {
    const view = officeNotificationView(notification);
    if (!notificationPortal()) {
        return view;
    }
    const links: NotificationLinkAction[] = [];
    for (const link of view.links) {
        const path = portalNotificationPath(link.path);
        if (path && !links.some((el) => el.path === path)) {
            links.push({...link, path});
        }
    }
    return {...view, path: portalNotificationPath(view.path), links, acquisitionLists: false};
};

export const isMention = (notification: NotificationLike): boolean => notification.type === NotificationTypes.MENTION;

/** To-do id of a to-do notification (assigned, due, overdue, completed) */
export const toDoIdOf = (notification: NotificationLike): string | undefined => {
    const id = payloadOf(notification).toDoId;
    return id ? str(id) : undefined;
};

export const filterSegment = (notifications: NotificationLike[], segment: NotificationSegment): NotificationLike[] => {
    switch (segment) {
        case 'dismissed':
            return notifications.filter((el) => !!el.dismissedAt);
        case 'mentions':
            return notifications.filter((el) => !el.dismissedAt && isMention(el));
        default:
            return notifications.filter((el) => !el.dismissedAt);
    }
};

/** The time shown for a notification: in the trash when it was deleted, otherwise when it was created */
export const shownDate = (notification: NotificationLike): Date =>
    (notification.dismissedAt || notification.createdAt) as Date;

export const relativeTime = (notification: NotificationLike, now: number = Date.now()): string => {
    const fromNow = moment(shownDate(notification)).from(moment(now));
    return notification.dismissedAt ? `${fromNow} gelöscht` : fromNow;
};

export const dayLabel = (date: Date, now = moment()): string => {
    const day = moment(date);
    if (day.isSame(now, 'day')) {
        return 'Heute';
    }
    if (day.isSame(now.clone().subtract(1, 'day'), 'day')) {
        return 'Gestern';
    }
    return day.format(day.isSame(now, 'year') ? 'dddd, D. MMMM' : 'dddd, D. MMMM YYYY');
};

/** Newest first; in the trash by deletion time */
export const sortForSegment = (notifications: NotificationLike[]): NotificationLike[] =>
    [...notifications].sort((a, b) => moment(shownDate(b)).valueOf() - moment(shownDate(a)).valueOf());

export type NotificationEntryGroup = {
    key: string;
    label: string;
    entries: NotificationEntry[];
};

/** Groups the (already sorted) entries by the day of their newest notification */
export const groupEntriesByDay = (entries: NotificationEntry[], now = moment()): NotificationEntryGroup[] => {
    const groups: NotificationEntryGroup[] = [];
    for (const entry of entries) {
        const date = shownDate(entry.notification);
        const key = moment(date).format('YYYY-MM-DD');
        const last = groups[groups.length - 1];
        if (last && last.key === key) {
            last.entries.push(entry);
        } else {
            groups.push({key, label: dayLabel(date, now), entries: [entry]});
        }
    }
    return groups;
};
